import { useEffect, useRef, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { adapter } from "@/services/db";
import { uploadFileResumable } from "@/services/storage";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

// Profile photo + cover photo for a professional, uploaded straight from Command Centre.
//   professionals/{id}.profile_image_url (+ photo_url, which Brugo Pro's field login uses) — the face
//   professionals/{id}.cover_image_url — the wide banner brugo.in shows on the pro's card/profile
//   *_meta: { width, height, bytes, type, uploaded_at, uploaded_by }
// Shows each image's resolution and size, and checks it against what looks good on brugo.in / Brugo Pro.
const KINDS = {
  profile: {
    label: "Profile photo", field: "profile_image_url", meta: "profile_image_meta", also: ["photo_url"],
    ratio: 1, ratioLabel: "square (1:1)", rec: [800, 800], min: [400, 400], maxSide: 1200,
    tip: "Face in the middle, plain background — it's shown as a circle.",
  },
  cover: {
    label: "Cover photo", field: "cover_image_url", meta: "cover_image_meta", also: [],
    ratio: 16 / 9, ratioLabel: "wide (16:9)", rec: [1600, 900], min: [1200, 675], maxSide: 1920,
    tip: "Their best finished work, landscape. Shown across the top of their brugo.in card.",
  },
  portfolio: {
    label: "Portfolio photo", ratio: 1, ratioLabel: "square (1:1)", rec: [1200, 1200], min: [600, 600], maxSide: 1600,
    tip: "Finished work they did. Shown as square tiles in the Portfolio section of their brugo.in profile.",
  },
};
const MAX_PORTFOLIO = 12;
const urlOf = (it) => (typeof it === "string" ? it : it?.url || it?.image_url || it?.src || null);
// Portfolio switch per professional (professionals/{id}.portfolio_enabled). Not set yet → treated
// as ON only if they already have portfolio photos (so nothing already live disappears).
export const portfolioOn = (pro) => (typeof pro?.portfolio_enabled === "boolean"
  ? pro.portfolio_enabled
  : (Array.isArray(pro?.portfolio) && pro.portfolio.some(urlOf)));
const MAX_BYTES = 5 * 1024 * 1024;
const fmtBytes = (b) => (b == null ? "—" : b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

function readDims(src) {
  return new Promise((res) => {
    const img = new Image();
    img.onload = () => res({ width: img.naturalWidth, height: img.naturalHeight, img });
    img.onerror = () => res(null);
    img.src = src;
  });
}

// Checks shown next to a picked / current image.
export function checkImage(kind, { width, height, bytes }) {
  const k = KINDS[kind];
  const out = [];
  if (width && height) {
    if (width < k.min[0] || height < k.min[1]) out.push({ ok: false, text: `Small — at least ${k.min[0]} × ${k.min[1]} px looks sharp` });
    else out.push({ ok: true, text: `Resolution good (recommended ${k.rec[0]} × ${k.rec[1]})` });
    const r = width / height;
    if (Math.abs(r - k.ratio) / k.ratio > 0.12) out.push({ ok: false, text: `Shape is ${r >= 1 ? r.toFixed(2) + ":1" : "1:" + (1 / r).toFixed(2)} — best ${k.ratioLabel}; edges may be cropped` });
    else out.push({ ok: true, text: `Shape fits (${k.ratioLabel})` });
  }
  if (bytes != null) out.push(bytes > MAX_BYTES ? { ok: false, text: `${fmtBytes(bytes)} — over 5 MB, will be optimised` } : { ok: true, text: `File size ${fmtBytes(bytes)}` });
  return out;
}

// Resize to the kind's max side and re-encode as JPEG (~85%) when the file is big.
async function optimise(file, kind) {
  const url = URL.createObjectURL(file);
  try {
    const d = await readDims(url);
    if (!d) return file;
    const k = KINDS[kind];
    const scale = Math.min(1, k.maxSide / Math.max(d.width, d.height));
    const w = Math.round(d.width * scale), h = Math.round(d.height * scale);
    const c = document.createElement("canvas"); c.width = w; c.height = h;
    const ctx = c.getContext("2d"); ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, w, h); ctx.drawImage(d.img, 0, 0, w, h);
    const blob = await new Promise((r) => c.toBlob(r, "image/jpeg", 0.85));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], file.name.replace(/\.[a-z0-9]+$/i, "") + ".jpg", { type: "image/jpeg" });
  } finally { URL.revokeObjectURL(url); }
}

function PhotoSlot({ pro, kind }) {
  const k = KINDS[kind];
  const { user } = useAuth();
  const inputRef = useRef(null);
  const current = pro[k.field] || (kind === "profile" ? pro.photo_url : null) || null;
  const [curDims, setCurDims] = useState(null);
  const [pick, setPick] = useState(null); // { file, url, width, height, bytes }
  const [opt, setOpt] = useState(true);
  const [pct, setPct] = useState(null);

  useEffect(() => { let on = true; setCurDims(null); if (current) readDims(current).then((d) => on && setCurDims(d && { width: d.width, height: d.height })); return () => { on = false; }; }, [current]);
  useEffect(() => () => { if (pick?.url) URL.revokeObjectURL(pick.url); }, [pick]);

  const choose = async (file) => {
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) return toast.error("Use a JPG, PNG or WebP image");
    const url = URL.createObjectURL(file);
    const d = await readDims(url);
    if (!d) { URL.revokeObjectURL(url); return toast.error("Couldn't read that image"); }
    setOpt(file.size > 1.5 * 1024 * 1024 || Math.max(d.width, d.height) > k.maxSide);
    setPick({ file, url, width: d.width, height: d.height, bytes: file.size });
  };

  const upload = async () => {
    if (!pick) return;
    try {
      setPct(0);
      let file = pick.file;
      if (opt || file.size > MAX_BYTES) file = await optimise(file, kind);
      if (file.size > MAX_BYTES) { setPct(null); return toast.error("Still over 5 MB — pick a smaller image"); }
      const d = file === pick.file ? pick : await (async () => { const u = URL.createObjectURL(file); const x = await readDims(u); URL.revokeObjectURL(u); return x || pick; })();
      const ext = file.type === "image/png" ? "png" : file.type === "image/webp" ? "webp" : "jpg";
      const url = await uploadFileResumable(`app_content/professionals/${pro.id}/${kind}_${Date.now()}.${ext}`, file, setPct);
      const meta = { width: d.width, height: d.height, bytes: file.size, type: file.type, uploaded_at: new Date().toISOString(), uploaded_by: user?.email || null };
      const patch = { [k.field]: url, [k.meta]: meta, updated_at: new Date().toISOString() };
      k.also.forEach((f) => { patch[f] = url; });
      await adapter.update("professionals", pro.id, patch);
      adapter.add("activity_logs", { app: "ops", entity_type: "professional", entity_id: pro.id, action: `pro_${kind}_photo_uploaded`, change_type: "photo", description: `${k.label} updated (${d.width}×${d.height}, ${fmtBytes(file.size)})`, before: current || null, after: url, performed_by: user?.email || null, performed_by_role: user?.role || null, status: "applied", created_at: new Date().toISOString() }).catch(() => {});
      toast.success(`${k.label} updated — live on brugo.in and Brugo Pro`);
      setPick(null);
    } catch (e) {
      toast.error(e?.code === "permission-denied" ? "Permission denied — ops or admin role required" : e?.response?.data?.detail || e?.message || "Upload failed");
    } finally { setPct(null); }
  };

  const remove = async () => {
    try {
      const patch = { [k.field]: null, [k.meta]: null, updated_at: new Date().toISOString() };
      k.also.forEach((f) => { patch[f] = null; });
      await adapter.update("professionals", pro.id, patch);
      toast.success(`${k.label} removed`);
    } catch (e) { toast.error(e?.message || "Couldn't remove"); }
  };

  const shown = pick ? { url: pick.url, width: pick.width, height: pick.height, bytes: pick.bytes } : current ? { url: current, ...(curDims || {}), bytes: pro[k.meta]?.bytes ?? null } : null;
  const checks = shown ? checkImage(kind, shown) : [];
  return (
    <div className="rounded-lg border border-border p-3 space-y-2" data-testid={`pro-photo-${kind}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold">{k.label}</p>
        <span className="text-[11px] text-muted-foreground">Best {k.rec[0]} × {k.rec[1]} px · JPG/PNG/WebP · up to 5 MB</span>
      </div>
      <div className={cn("relative overflow-hidden rounded-lg border border-dashed border-border bg-secondary/40 flex items-center justify-center", kind === "profile" ? "h-32 w-32 rounded-full mx-auto" : "aspect-video w-full")}>
        {shown?.url ? <img src={shown.url} alt="" className="h-full w-full object-cover" data-testid={`pro-photo-${kind}-img`} /> : <Icons.ImagePlus className="h-6 w-6 text-muted-foreground" />}
        {pick && <span className="absolute top-1.5 left-1.5 rounded-full bg-amber-500 text-white text-[10px] px-2 py-0.5">Preview — not saved</span>}
      </div>
      {shown && (
        <div className="text-[11px] space-y-0.5" data-testid={`pro-photo-${kind}-info`}>
          <p className="font-mono text-foreground">{shown.width && shown.height ? `${shown.width} × ${shown.height} px` : "Reading size…"}{shown.bytes != null ? ` · ${fmtBytes(shown.bytes)}` : ""}{!pick && pro[k.meta]?.uploaded_at ? ` · uploaded ${new Date(pro[k.meta].uploaded_at).toLocaleDateString("en-IN")}` : ""}</p>
          {checks.map((c) => <p key={c.text} className={c.ok ? "text-emerald-700" : "text-amber-700"}>{c.ok ? "✓" : "⚠"} {c.text}</p>)}
        </div>
      )}
      <p className="text-[11px] text-muted-foreground">{k.tip}</p>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { choose(e.target.files?.[0]); e.target.value = ""; }} data-testid={`pro-photo-${kind}-input`} />
      {pick ? (
        <div className="space-y-2">
          <label className="flex items-center justify-between gap-2 text-xs"><span>Optimise before upload <span className="text-muted-foreground">(resize to {k.maxSide}px, smaller file)</span></span><Switch checked={opt} onCheckedChange={setOpt} data-testid={`pro-photo-${kind}-optimise`} /></label>
          <div className="flex gap-2">
            <Button size="sm" onClick={upload} disabled={pct != null} data-testid={`pro-photo-${kind}-save`}>{pct != null ? `Uploading ${pct}%…` : "Save photo"}</Button>
            <Button size="sm" variant="ghost" onClick={() => setPick(null)} disabled={pct != null}>Cancel</Button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => inputRef.current?.click()} data-testid={`pro-photo-${kind}-pick`}><Icons.Upload className="h-3.5 w-3.5 mr-1" />{current ? "Replace" : "Upload"}</Button>
          {current && <Button size="sm" variant="ghost" className="text-red-600" onClick={remove} data-testid={`pro-photo-${kind}-remove`}>Remove</Button>}
        </div>
      )}
    </div>
  );
}

// Small ON/OFF switch, used in the Professionals list and inside the drawer. Only this pro changes.
export function PortfolioSwitch({ pro, testId }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [local, setLocal] = useState(null);
  const on = local ?? portfolioOn(pro);
  useEffect(() => { setLocal(null); }, [pro.portfolio_enabled]);
  const toggle = async (v) => {
    setBusy(true); setLocal(v);
    try {
      await adapter.update("professionals", pro.id, { portfolio_enabled: v, updated_at: new Date().toISOString() });
      adapter.add("activity_logs", { app: "ops", entity_type: "professional", entity_id: pro.id, action: v ? "pro_portfolio_enabled" : "pro_portfolio_disabled", change_type: "setting", description: `Portfolio turned ${v ? "on" : "off"}`, performed_by: user?.email || null, performed_by_role: user?.role || null, status: "applied", created_at: new Date().toISOString() }).catch(() => {});
      toast.success(v ? "Portfolio on for this professional" : "Portfolio off: hidden on brugo.in");
    } catch (e) {
      setLocal(null);
      toast.error(e?.code === "permission-denied" ? "Permission denied — ops or admin role required" : e?.message || "Couldn't save");
    } finally { setBusy(false); }
  };
  return <Switch checked={on} disabled={busy} onCheckedChange={toggle} data-testid={testId || `pro-portfolio-switch-${pro.id}`} />;
}

// Portfolio photos, added from Command Centre like the profile / cover photo.
// Saved as professionals/{id}.portfolio = [{ url, type: "image", width, height, bytes, uploaded_at, uploaded_by }]
// (the same list shape Brugo Pro and brugo.in already read).
function PortfolioSection({ pro }) {
  const { user } = useAuth();
  const inputRef = useRef(null);
  const [pct, setPct] = useState(null);
  const on = portfolioOn(pro);
  const items = (Array.isArray(pro.portfolio) ? pro.portfolio : []).filter(urlOf);

  const save = async (next, log) => {
    await adapter.update("professionals", pro.id, { portfolio: next, updated_at: new Date().toISOString() });
    adapter.add("activity_logs", { app: "ops", entity_type: "professional", entity_id: pro.id, action: log, change_type: "photo", description: `Portfolio now has ${next.length} photo${next.length === 1 ? "" : "s"}`, performed_by: user?.email || null, performed_by_role: user?.role || null, status: "applied", created_at: new Date().toISOString() }).catch(() => {});
  };

  const addFiles = async (files) => {
    const list = Array.from(files || []).filter((f) => /^image\/(jpeg|png|webp)$/.test(f.type));
    if (!list.length) return toast.error("Use JPG, PNG or WebP images");
    const room = MAX_PORTFOLIO - items.length;
    if (room <= 0) return toast.error(`Up to ${MAX_PORTFOLIO} portfolio photos`);
    const take = list.slice(0, room);
    try {
      const added = [];
      for (let i = 0; i < take.length; i++) {
        setPct(Math.round((i / take.length) * 100));
        let file = await optimise(take[i], "portfolio");
        if (file.size > MAX_BYTES) { toast.error(`${take[i].name} is still over 5 MB, skipped`); continue; }
        const u = URL.createObjectURL(file); const d = await readDims(u); URL.revokeObjectURL(u);
        const ext = file.type === "image/png" ? "png" : file.type === "image/webp" ? "webp" : "jpg";
        const url = await uploadFileResumable(`app_content/professionals/${pro.id}/portfolio_${Date.now()}_${i}.${ext}`, file, () => {});
        added.push({ url, type: "image", width: d?.width || null, height: d?.height || null, bytes: file.size, uploaded_at: new Date().toISOString(), uploaded_by: user?.email || null });
      }
      if (added.length) {
        await save([...items, ...added], "pro_portfolio_photos_added");
        toast.success(`${added.length} portfolio photo${added.length === 1 ? "" : "s"} added`);
      }
      if (list.length > room) toast.message(`Only ${room} added: the limit is ${MAX_PORTFOLIO}`);
    } catch (e) {
      toast.error(e?.code === "permission-denied" ? "Permission denied — ops or admin role required" : e?.message || "Upload failed");
    } finally { setPct(null); }
  };

  const removeAt = async (i) => {
    try { await save(items.filter((_, j) => j !== i), "pro_portfolio_photo_removed"); toast.success("Photo removed"); }
    catch (e) { toast.error(e?.message || "Couldn't remove"); }
  };
  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items]; [next[i], next[j]] = [next[j], next[i]];
    try { await save(next, "pro_portfolio_reordered"); } catch (e) { toast.error(e?.message || "Couldn't reorder"); }
  };

  return (
    <div className="rounded-lg border border-border p-3 space-y-2" data-testid="pro-portfolio">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold">Portfolio</p>
          <p className="text-[11px] text-muted-foreground">{on ? `On · ${items.length}/${MAX_PORTFOLIO} photos · shown on brugo.in` : "Off for this professional · no portfolio on brugo.in"}</p>
        </div>
        <PortfolioSwitch pro={pro} testId="pro-portfolio-switch" />
      </div>
      {on && (
        <>
          {items.length > 0 ? (
            <div className="grid grid-cols-3 gap-2" data-testid="pro-portfolio-grid">
              {items.map((it, i) => (
                <div key={urlOf(it) + i} className="group relative aspect-square overflow-hidden rounded-md border border-border bg-secondary/40">
                  <img src={urlOf(it)} alt="" className="h-full w-full object-cover" />
                  <div className="absolute inset-x-0 bottom-0 flex justify-between gap-1 bg-black/55 p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="text-white disabled:opacity-30" title="Move left"><Icons.ChevronLeft className="h-4 w-4" /></button>
                    <button type="button" onClick={() => removeAt(i)} className="text-white" title="Remove" data-testid={`pro-portfolio-remove-${i}`}><Icons.Trash2 className="h-4 w-4" /></button>
                    <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} className="text-white disabled:opacity-30" title="Move right"><Icons.ChevronRight className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-[11px] text-muted-foreground">No portfolio photos yet.</p>}
          <p className="text-[11px] text-muted-foreground">{KINDS.portfolio.tip} Best {KINDS.portfolio.rec[0]} × {KINDS.portfolio.rec[1]} px · large files are resized automatically.</p>
          <input ref={inputRef} type="file" multiple accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="pro-portfolio-input" />
          <Button size="sm" variant="outline" onClick={() => inputRef.current?.click()} disabled={pct != null || items.length >= MAX_PORTFOLIO} data-testid="pro-portfolio-add">
            <Icons.Upload className="h-3.5 w-3.5 mr-1" />{pct != null ? `Uploading ${pct}%…` : "Add photos"}
          </Button>
        </>
      )}
    </div>
  );
}

export function ProPhotos({ pro }) {
  return (
    <div className="mt-4 space-y-3" data-testid="pro-photos">
      <h4 className="font-heading font-semibold text-foreground flex items-center gap-2 text-sm"><Icons.Camera className="h-4 w-4 text-primary" /> Photos</h4>
      <PhotoSlot key={`p-${pro.id}`} pro={pro} kind="profile" />
      <PhotoSlot key={`c-${pro.id}`} pro={pro} kind="cover" />
      <PortfolioSection key={`f-${pro.id}`} pro={pro} />
    </div>
  );
}
